import { FC, useEffect, useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { IProject } from '../../core/interfaces/project.interface';
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  CardMedia,
  IconButton,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { DataGrid, GridColDef, GridValueFormatterParams } from '@mui/x-data-grid';
import { DownloadAOIComponent } from '../DownloadAIO';
import { OpenAOIComponent } from '../OpenAOI';
import { Download, Edit, Info, OpenInNew } from '@mui/icons-material';
import { ProjectUpdateModal } from '../ProjectUpdateModal';
import { FailedStatus, isProjectFailed } from '../FailedStatus';
import { useProgress } from '../../core/hooks';
import useGentian from '../../core/hooks/useGentian';

export interface PendingProjectsProps {
  projects: IProject[];
  onRowSelect?: (project: IProject) => void;
  onEditProject?: (project: IProject) => void;
}

export const PendingProjects: FC<PendingProjectsProps> = ({ projects, onRowSelect, onEditProject }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { data: progressData } = useProgress();
  const { notifyCustomer } = useGentian();
  const notifiedProjectsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    console.log('Progress data updated:', progressData);
    if (progressData) {
      console.log(
        `[Progress Log] Project: ${progressData.processId}, Status: ${progressData.status}, Progress: ${Math.round(
          progressData.progress * 100,
        )}%`,
      );
    }
  }, [progressData]);

  const notifySentRef = useRef<{ [key: string]: boolean }>({});

  useEffect(() => {
    if (!progressData || !projects?.length) return;

    const parts = progressData.statusUrl.split('/');
    const projectUuid = parts[parts.length - 1];

    const isComplete = progressData.progress >= 0.99;
    if (!isComplete) return;

    const project = projects.find((p) => p._id === projectUuid);

    // 🛑 No project yet → wait
    if (!project) return;

    // 🛑 Already has result
    if (project.hasResult) return;

    // 🛑 Backend truth
    if (project.notifiedAt) {
      notifySentRef.current[projectUuid] = true;
      return;
    }

    // 🛑 Session guard
    if (notifySentRef.current[projectUuid]) return;

    notifySentRef.current[projectUuid] = true;

    notifyCustomer.mutate(projectUuid, {
      onSuccess: () => {
        console.log(`Notification email sent for project ${projectUuid}`);
      },
    });
  }, [progressData, projects]);

  const [searchText, setSearchText] = useState('');
  const [codeEditModalOpen, setCodeEditModalOpen] = useState(false);
  const [project, setProject] = useState<IProject>(undefined);

  // ✅ Extract just the UUID from the SSE statusUrl
  const extractUuid = (url: string): string | null => {
    if (!url) return null;
    const parts = url.split('/');
    return parts[parts.length - 1]; // take last path segment
  };

  const handleSearchChange = (event) => {
    setSearchText(event.target.value.toString());
  };

  const columns = useRef<GridColDef[]>();
  columns.current = [
    {
      field: 'id',
      headerName: 'Id',
      headerAlign: 'center',
      disableColumnMenu: true,
    },
    {
      field: 'AOIThumbnail',
      headerAlign: 'center',
      headerName: '',
      sortable: false,
      groupable: false,
      filterable: false,
      hideable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
          <img
            src={params.value}
            alt="Project Thumbnail"
            style={{
              maxWidth: '80px',
              maxHeight: '42px',
              objectFit: 'cover',
              borderRadius: '4px',
            }}
          />
        </Box>
      ),
    },
    {
      field: 'name',
      headerName: 'Name',
      minWidth: 128,
      headerAlign: 'center',
      flex: 1,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'space-between' }}>
          <Typography
            sx={{
              display: '-webkit-box',
              overflow: 'hidden',
              WebkitBoxOrient: 'vertical',
              WebkitLineClamp: 3,
              textOverflow: 'ellipsis',
              whiteSpace: 'normal',
              height: 'fit-content',
              textWrap: 'wrap',
              fontSize: '12px',
            }}
          >
            {params.value}
          </Typography>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setProject(params.row);
              setCodeEditModalOpen(true);
            }}
          >
            <Tooltip title="Edit project name">
              <Edit />
            </Tooltip>
          </IconButton>
        </Box>
      ),
    },
    {
      field: 'code',
      headerName: 'Code',
      width: 96,
      headerAlign: 'center',
      disableColumnMenu: true,
    },
    {
      field: 'requestDate',
      headerName: 'Request Date',
      maxWidth: 160,
      minWidth: 96,
      align: 'center',
      flex: 1,
      valueFormatter: (params: GridValueFormatterParams) => new Date(params.value).toLocaleDateString(),
      headerAlign: 'center',
      disableColumnMenu: true,
    },
    {
      field: 'download',
      headerName: 'Download Boundary',
      width: 96,
      sortable: false,
      groupable: false,
      filterable: false,
      hideable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
          <DownloadAOIComponent AOI={params.value} variant="IconOnly" />
        </Box>
      ),
      headerAlign: 'center',
    },
    {
      field: 'open',
      headerName: 'Open Requests',
      sortable: false,
      groupable: false,
      filterable: false,
      hideable: false,
      width: 96,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
          <OpenAOIComponent AOI={params.value} variant="IconOnly" />
        </Box>
      ),
      headerAlign: 'center',
    },
    {
      field: 'result',
      headerName: 'Open Result',
      width: 180,
      disableColumnMenu: true,
      renderCell: (params) => {
        const projectUuid = params.row._id;
        const progressUuid = extractUuid(progressData?.statusUrl);

        const progressComplete = progressData && progressUuid === projectUuid && progressData.progress === 1;
        const progressPercent =
          progressData && progressUuid === projectUuid ? Math.round(progressData.progress * 100) : 0;
        // console.log('Rendering result cell:', {
        //   projectUuid,
        //   progressUuid,
        //   progressData,
        //   progressComplete,
        // });

        return (
          <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
            {params.row.hasResult || progressComplete ? (
              <Button
                variant="contained"
                component={RouterLink}
                to={`/survey-result/${projectUuid}`}
                endIcon={<OpenInNew />}
                sx={{
                  display: { xs: 'none', sm: 'inline-flex' },
                  textAlign: 'center',
                  backgroundColor: 'var(--orchid-purple)',
                  color: 'var(--gentian-white)',
                  '&:hover': { backgroundColor: 'var(--eclipse-black)' },
                }}
              >
                Open Result
              </Button>
            ) : isProjectFailed(params.row, progressData) ? (
              <FailedStatus />
            ) : (
              <>
                Pending {progressPercent > 0 ? `${progressPercent}%` : ''}
                {progressPercent < 99 && progressPercent > 0 && (
                  <Tooltip title="This project is still processing. This could take up to 1 hour.">
                    <Info sx={{ fontSize: '16px', color: 'gray', ml: 0.5 }} />
                  </Tooltip>
                )}
              </>
            )}
          </Box>
        );
      },
      headerAlign: 'center',
    },
  ];

  const filteredRows = projects
    .map((r) => ({
      ...r,
      download: r.AOI,
      open: r.AOI,
      id: r._id,
      result: r._id,
      downloadResult: r.deliverableUrl,
    }))
    .filter(
      (row) =>
        row.name.includes(searchText) || new Date(row.requestDate).toLocaleDateString().includes(searchText),
    );

  return !isMobile ? (
    <Box>
      {codeEditModalOpen && (
        <ProjectUpdateModal
          field="name"
          project={project}
          onClose={() => setCodeEditModalOpen(false)}
          onSaveClick={(value) => {
            project.name = value;
            onEditProject?.(project);
            setCodeEditModalOpen(false);
          }}
        />
      )}
      <TextField
        value={searchText}
        onChange={handleSearchChange}
        placeholder="Search by name or date"
        variant="standard"
        sx={{ width: '256px', mt: 2 }}
      />

      <DataGrid
        sx={{
          '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
            outline: 'none',
          },
          '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': {
            outline: 'none',
          },
        }}
        className="pending-projects-table"
        rows={filteredRows}
        columns={columns.current}
        onRowClick={(params) => {
          const project = projects.find((p) => p._id === params.id);
          onRowSelect?.(project);
        }}
        disableColumnFilter
        initialState={{
          pagination: {
            paginationModel: {
              pageSize: 10,
            },
          },
          sorting: {
            sortModel: [
              {
                field: 'requestDate',
                sort: 'desc',
              },
            ],
          },
          columns: {
            columnVisibilityModel: {
              id: false,
            },
          },
        }}
        pageSizeOptions={[5, 10, 20, 50, 100]}
        disableRowSelectionOnClick
      />
    </Box>
  ) : (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {projects.map((project) => (
        <Box key={project._id} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Card
            sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
              flexDirection: 'column',
              mt: 'var(--content-spacing-narrow)',
            }}
          >
            <CardHeader title={project.name} />
            <CardMedia image={project.AOIThumbnail} title={project.name} component="img" />
            <CardContent>
              <Box sx={{ width: '100%', display: 'flex', flexDirection: 'row' }}>
                {`Request date: ${new Date(project.requestDate).toLocaleDateString()}`}
              </Box>
            </CardContent>
            <CardActions>
              <Box>
                <DownloadAOIComponent AOI={project.AOI} />
              </Box>
              <Box>
                <OpenAOIComponent AOI={project.AOI} />
              </Box>
              <Box>
                <Button
                  variant="contained"
                  onClick={() => (window.location.href = `/survey-result/${project._id}`)}
                >
                  Result <OpenInNew />
                </Button>
              </Box>
            </CardActions>
          </Card>
        </Box>
      ))}
    </Box>
  );
};
